import React from "react";
import Barcode from "react-barcode";
import { QRCodeSVG as QRCode } from "qrcode.react";
import {
  Modal,
  Paper,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Divider,
  Switch,
  FormControlLabel,
  Link,
} from "@mui/material";
import {
  Print as PrintIcon,
  Close as CloseIcon,
  QrCode as QrCodeIcon,
  BrandingWatermark as BarcodeIcon,
  ContentCopy as CopyIcon,
  Timer as TimerIcon,
  PhoneAndroid as PhoneIcon,
} from "@mui/icons-material";

const TaskBarcodeModal = ({
  open,
  onClose,
  task,
  isDarkMode,
  onStartTimer,
  onStopTimer,
  onResetTimer,
}) => {
  const [tabValue, setTabValue] = React.useState(0);
  const [showMobileHelp, setShowMobileHelp] = React.useState(false);

  if (!task) return null;

  const appDeepLink = `gabs-mobile://task/${task._id}/${task.aufgabeId}`;
  const apiEndpoint = `${window.location.origin}/api/tasks/${task._id}`;

  const themeColors = isDarkMode
    ? {
        background: "#1a202c",
        card: "#2d3748",
        text: "#f7fafc",
        textSecondary: "#a0aec0",
        primary: "#4361ee",
        taskBorder: "#4a5568",
        codeBackground: "#1e293b",
      }
    : {
        background: "#f8f9fd",
        card: "#ffffff",
        text: "#2d3748",
        textSecondary: "#718096",
        primary: "#4361ee",
        taskBorder: "#e2e8f0",
        codeBackground: "#f5f8ff",
      };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const printCode = () => {
    const codeType = tabValue === 0 ? "barcode" : "qrcode";
    const printWindow = window.open("", "PRINT", "height=800,width=800");

    printWindow.document.write(`
      <html>
        <head>
          <title>Print ${codeType === "barcode" ? "Barcode" : "QR Code"}</title>
          <style>
            @page { size: 2in 1in; margin: 8mm; }
            body {
              width: 2in;
              margin: 0;
              padding: 0;
              line-height: 1;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              font-family: Arial, sans-serif;
            }
            .code-container {
              text-align: center;
              margin-bottom: 5px;
            }
            .task-info {
              font-size: 10px;
              color: #666;
              margin-top: 5px;
              text-align: center;
            }
            .task-id {
              font-weight: bold;
              font-size: 12px;
              margin-top: 5px;
            }
          </style>
        </head>
        <body>
          <div class="code-container">
            ${document.getElementById(codeType + "-container").innerHTML}
          </div>
          <div class="task-info">
            <div class="task-id">${task.aufgabeId}</div>
            <p>${task.title || ""}</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const copyApiEndpoint = () => {
    navigator.clipboard.writeText(apiEndpoint);
    alert("API Endpoint URL copied to clipboard");
  };

  const isTimerRunning = task.startTime && !task.endTime;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="barcode-modal-title">
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "500px",
          borderRadius: 4,
          p: 0,
          outline: "none",
          bgcolor: themeColors.card,
          color: themeColors.text,
          border: isDarkMode ? `1px solid ${themeColors.taskBorder}` : "none",
          textAlign: "center",
          overflow: "hidden",
        }}
        elevation={isDarkMode ? 0 : 5}
      >
        <Box
          sx={{
            bgcolor: themeColors.primary,
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">{task.aufgabeId}</Typography>
          <Button
            size="small"
            onClick={onClose}
            sx={{ color: "white", minWidth: "auto", p: 0.5 }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {task.title || "Unnamed Task"}
          </Typography>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          centered
          sx={{
            mb: 2,
            "& .MuiTab-root": { minWidth: 120, fontSize: "0.85rem" },
          }}
        >
          <Tab
            icon={<BarcodeIcon fontSize="small" />}
            label="Barcode"
            id="tab-barcode"
          />
          <Tab
            icon={<QrCodeIcon fontSize="small" />}
            label="QR Code"
            id="tab-qrcode"
          />
        </Tabs>

        <Box
          role="tabpanel"
          hidden={tabValue !== 0}
          id="tabpanel-barcode"
          sx={{ p: 2, pt: 0 }}
        >
          <Box
            id="barcode-container"
            sx={{
              my: 2,
              p: 3,
              bgcolor: "white",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Barcode
              value={task.aufgabeId}
              width={1.5}
              height={50}
              fontSize={12}
              margin={10}
            />
          </Box>
        </Box>

        <Box
          role="tabpanel"
          hidden={tabValue !== 1}
          id="tabpanel-qrcode"
          sx={{ p: 2, pt: 0 }}
        >
          <Box
            id="qrcode-container"
            sx={{
              my: 2,
              p: 3,
              bgcolor: "white",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <QRCode
              value={appDeepLink}
              size={150}
              level={"H"}
              includeMargin={true}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={showMobileHelp}
                onChange={(e) => setShowMobileHelp(e.target.checked)}
                color="primary"
              />
            }
            label="Mobile App Integration"
            sx={{
              mx: "auto",
              mb: 1,
              display: "flex",
              justifyContent: "center",
            }}
          />
        </Box>

        {showMobileHelp && (
          <Box
            sx={{
              p: 2,
              bgcolor: themeColors.codeBackground,
              borderTop: `1px solid ${themeColors.taskBorder}`,
              textAlign: "left",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              <PhoneIcon
                fontSize="small"
                sx={{ mr: 1, verticalAlign: "middle" }}
              />
              Mobile App Integration
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Scan the QR code with the Gabs mobile app to access this task
              directly.
            </Typography>

            <Box
              sx={{
                p: 1.5,
                bgcolor: isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.05)",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.7rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Box sx={{ wordBreak: "break-all" }}>{apiEndpoint}</Box>
              <Button
                size="small"
                onClick={copyApiEndpoint}
                sx={{ minWidth: "auto", ml: 1 }}
              >
                <CopyIcon fontSize="small" />
              </Button>
            </Box>

            <Typography variant="caption" color={themeColors.textSecondary}>
              Use this API endpoint to track time from the mobile app.
              <Link href="#" sx={{ display: "block", mt: 0.5 }}>
                Learn more about mobile integration
              </Link>
            </Typography>
          </Box>
        )}

        <Divider />
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TimerIcon
              fontSize="small"
              sx={{
                mr: 1,
                color: isTimerRunning ? "#f39c12" : themeColors.textSecondary,
              }}
            />
            <Typography variant="body2">
              {isTimerRunning ? "Timer läuft" : "Timer starten"}
            </Typography>
          </Box>

          <Box>
            {isTimerRunning ? (
              <Button
                variant="contained"
                color="warning"
                onClick={() => onStopTimer(task.id)}
                sx={{
                  mr: 1,
                  bgcolor: "#f39c12",
                  "&:hover": {
                    bgcolor: "#e67e22",
                  },
                }}
              >
                Timer stoppen
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={() => onStartTimer(task.id)}
                sx={{
                  mr: 1,
                  bgcolor: "#2ecc71",
                  "&:hover": {
                    bgcolor: "#27ae60",
                  },
                }}
              >
                Timer starten
              </Button>
            )}

            {task.startTime && (
              <Button
                variant="outlined"
                onClick={() => onResetTimer && onResetTimer(task.id)}
                sx={{
                  mr: 1,
                  color: themeColors.warning || "#f39c12",
                  borderColor: themeColors.warning || "#f39c12",
                  "&:hover": {
                    bgcolor: isDarkMode
                      ? "rgba(243, 156, 18, 0.1)"
                      : "rgba(243, 156, 18, 0.08)",
                    borderColor: themeColors.warning || "#f39c12",
                  },
                }}
              >
                Timer zurücksetzen
              </Button>
            )}

            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={printCode}
              sx={{
                bgcolor: themeColors.primary,
                color: "white",
                borderColor: themeColors.primary,
                "&:hover": {
                  bgcolor: isDarkMode ? "#374fc1" : "#324bbc",
                  borderColor: isDarkMode ? "#374fc1" : "#324bbc",
                },
              }}
            >
              Drucken
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.04)",
            borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"}`,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="text"
            startIcon={<CloseIcon />}
            onClick={onClose}
            sx={{ color: themeColors.textSecondary }}
          >
            Schließen
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TaskBarcodeModal;
